/**
 * The referral program's closed lists and published numbers, both halves (docs/PLAN-referral-program-20261019.md).
 *
 * functions/src/referrals/referralVocabulary.ts is the server's copy and src/constants/referralVocabulary.ts the
 * browser's. `functions/` and `src/` cannot import each other, so the two are byte-identical and every list is equal at
 * run time (tests/referrals/referralVocabulary.parity.test.ts). Each side takes its words from here, never a union of its
 * own: a word one side writes that the other cannot read is how they silently drift apart. The program's numbers live
 * here for the same reason: the pages promise exactly what the server pays.
 */

/**
 * The terms a referrer accepts: their effective date (src/config/legalVersions.ts `referral`), with `.N` for a later
 * version published the same day. A new version that takes anything away from referrers asks everyone to accept again;
 * one that only adds to what they earn keeps the version before it honored (REFERRAL_TERMS_STILL_HONORED).
 */
export const REFERRAL_TERMS_VERSION = '2026-11-03.2';

/**
 * Earlier versions of the standard terms whose acceptance still keeps a referrer's link working: those the current
 * version only adds to, so whoever accepted one keeps their link and earns under the current program without signing
 * again. referralCore.referralTermsCurrent is the one place a version is compared; a version that takes anything away
 * from one leaves it off this list, and its referrers are asked to sign again.
 *
 * The current version (2026-11-03.2; Miles, 2026-11-03: "every Prest0n employee a new organization hires in its first 2
 * months earns you $1,000") counts the Prest0n employees an organization hires in its first 2 months, where the version
 * of the same morning (2026-11-03) counted its first 12: that takes hires away, so 2026-11-03 is not honored and its
 * referrers sign again. The fourth version (2026-10-30) paid at most $1,000 for the organization, which the current
 * version always pays too (its first Prest0n employee is hired at its first payment, inside the window), so it only adds
 * to that one and 2026-10-30 stays honored.
 */
export const REFERRAL_TERMS_STILL_HONORED: readonly string[] = ['2026-10-30'];

/** What each moment pays a referrer for one Prest0n employee the referred organization hires, in cents. */
export const REFERRAL_REWARD_CENTS = { month1: 50_000, month3: 50_000, annual: 100_000 } as const;

/**
 * What each Prest0n employee (one seat of a Prest0n plan) a referred new organization hires earns its referrer in all,
 * in cents: $500 at that employee's first paid month and $500 at its third, or $1,000 at once on an annual plan. There
 * is no limit per organization (ruling R13g, Miles, 2026-11-02).
 */
export const REFERRAL_REWARD_PER_HIRE_CENTS = 100_000;

/**
 * A Prest0n employee earns when the referred organization first pays for it within this many months of its first
 * counted payment. Its schedule then completes however late its third month comes; one first paid for later earns nothing.
 * Two since 2026-11-03.2 (Miles: "in its first 2 months ... (not year)"); a referral whose window was counted before keeps
 * the window stored with it (referralCore: hireWindowEndsSec).
 */
export const REFERRAL_HIRE_WINDOW_MONTHS = 2;

/**
 * The most a referral counted under the program before 2026-11-03 can earn its referrer, in cents: its organization's
 * $1,000, however many Prest0n employees it hires. Only a referral whose first payment was counted before the per-hire
 * program reads it (referralCore.rewardPlanOf); every later one has no cap.
 */
export const REFERRAL_LEGACY_ORG_CAP_CENTS = 100_000;

/**
 * How a referral earns. `per-hire`: every Prest0n employee its organization hires in its first 2 months earns $1,000, each
 * on its own schedule (every referral whose first payment is counted on or after 2026-11-03). `per-organization`: the
 * program before it, at most $1,000 for the organization (a referral whose first payment was counted before).
 */
export const REFERRAL_REWARD_PLANS = ['per-hire', 'per-organization'] as const;
export type ReferralRewardPlan = (typeof REFERRAL_REWARD_PLANS)[number];

/**
 * What the person who signs up through a link starts with: an extended internship, one month with 420 pr0 of work
 * (priced at the Standard Rate; every internship runs one month since 2026-11-03, and an extended one carries more
 * work). functions/src/__tests__/internshipTerms.test.ts holds it equal to that arithmetic (accessCodeRedemption.ts
 * extendedInternshipCreditCents).
 */
export const REFERRED_OFFER = { creditAmountCents: 91_560, durationDays: 30 } as const;

/** A link click stays attributable this long on the device that clicked it. */
export const REFERRAL_ATTRIBUTION_DAYS = 30;

/**
 * A reward is credited this many days after the payment that earns it, unless that payment is refunded or disputed
 * first (a refund takes its share, a dispute all of it, before any money moves). A stolen card's chargeback usually
 * comes within this window, so a reward cannot leave as cash before the payment behind it has held.
 */
export const REFERRAL_CLEARING_DAYS = 30;

/** A new account may take a link's offer for this many days after it was made (the claim normally runs at once). */
export const REFERRAL_NEW_ACCOUNT_DAYS = 3;

/** Where the program lives on the web. */
export const REFERRAL_SITE_ORIGIN = 'https://prest0.ai';

/** A code's link and short link. */
export function referralLinks(code: string): { link: string; shortLink: string } {
  return {
    link: `${REFERRAL_SITE_ORIGIN}/referrals/join/${code}`,
    shortLink: `${REFERRAL_SITE_ORIGIN}/r/${code}`,
  };
}

/**
 * A code as typed, pasted or read off a link: case and separators do not matter. Null when it cannot be a code, so a
 * lookup never runs on junk.
 */
export function normalizeReferralCode(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase().replace(/[\s-]/g, '');
  return /^[A-Z0-9]{6,12}$/.test(code) ? code : null;
}

/** Where a referred organization stands in the program. */
export const REFERRAL_STATUSES = [
  // The account was made through the link; its extended internship is being set up.
  'signed-up',
  // The referred organization is in its extended internship.
  'trial',
  // The referred organization has paid at least one invoice.
  'hired',
  // Every reward this organization can earn has been earned: each Prest0n employee's $1,000 once its hire window has
  // passed (a referral counted before 2026-11-03: its organization's $1,000).
  'completed',
  // No longer earns rewards (a payment refunded in full, a chargeback, or a review found it was not a real new
  // organization). A reward another payment earned that is still clearing or held is paid unless a review ended it.
  'void',
] as const;
export type ReferralStatus = (typeof REFERRAL_STATUSES)[number];

/**
 * The three moments a referrer is paid for one Prest0n employee (a referral counted before 2026-11-03: for its
 * organization). A ledger keys the first employee's by the moment alone, as every referral did before, and each later
 * employee's as `moment@N`, N its place among the organization's Prest0n hires (referralCore.rewardKey).
 */
export const REFERRAL_MILESTONES = ['month1', 'month3', 'annual'] as const;
export type ReferralMilestone = (typeof REFERRAL_MILESTONES)[number];

/** What became of a milestone's reward. */
export const REFERRAL_MILESTONE_STATES = [
  // Credited to the referrer's wallet, withdrawable.
  'paid',
  // Taken back, whole or in part, because the payment behind it was refunded or disputed.
  'reversed',
  // Earned but not yet credited: held while the referrer's participation is suspended, payouts are paused or the
  // day's price cannot be read, and credited (less any share refunded meanwhile) once that ends and it has cleared.
  'withheld',
  // Earned and on its way: credited automatically REFERRAL_CLEARING_DAYS after the payment that earned it (less any
  // share refunded meanwhile; a dispute takes it all).
  'clearing',
] as const;
export type ReferralMilestoneState = (typeof REFERRAL_MILESTONE_STATES)[number];

/** A referrer's standing in the program. */
export const REFERRER_STATUSES = ['active', 'suspended'] as const;
export type ReferrerStatus = (typeof REFERRER_STATUSES)[number];

/** Why a new account could not take a referral offer (the guardrails, in the order they are checked). */
export const REFERRAL_CLAIM_REFUSALS = [
  'program-paused',
  'code-unknown',
  'referrer-inactive',
  'self-referral',
  'already-referred',
  'not-a-new-account',
  'already-started',
  'joined-an-organization',
  'email-already-referred',
  'device-already-referred',
  'device-unknown',
  // More new accounts took referral offers from this network today than a real office makes.
  'network-limit',
] as const;
export type ReferralClaimRefusal = (typeof REFERRAL_CLAIM_REFUSALS)[number];

/** Signals a review should look at; none of them stops a referral by itself. */
export const REFERRAL_FLAGS = [
  // The new account signed up from the same network as the referrer.
  'same-network-as-referrer',
  // A payment behind a reward was refunded.
  'refunded',
  // A payment behind a reward was disputed.
  'disputed',
  // A reversal could not take the whole reward back (it had already been withdrawn or spent).
  'reversal-shortfall',
] as const;
export type ReferralFlag = (typeof REFERRAL_FLAGS)[number];

/** What the page may ask the referralProgram callable to do. */
export const REFERRAL_PROGRAM_ACTIONS = [
  'status',
  'enroll',
  'claim',
  'org-overview',
  'admin-overview',
  'admin-void-referral',
  'admin-suspend-referrer',
  'admin-reinstate-referrer',
] as const;
export type ReferralProgramAction = (typeof REFERRAL_PROGRAM_ACTIONS)[number];

/** The program review's two lists (admin-overview), each read a page at a time. */
export const REFERRAL_REVIEW_LISTS = ['referrers', 'referrals'] as const;
export type ReferralReviewList = (typeof REFERRAL_REVIEW_LISTS)[number];
